import _async_to_generator from "@swc/helpers/src/_async_to_generator.mjs";
import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_without_properties from "@swc/helpers/src/_object_without_properties.mjs";
import _sliced_to_array from "@swc/helpers/src/_sliced_to_array.mjs";
import regeneratorRuntime from "/builds/lululemon/global-digital-tech/content-and-education/ecom-web-app/node_modules/next/dist/compiled/regenerator-runtime/runtime.js";
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import debounce from "lodash/debounce";
import { isDesktop, isMobile, isTablet, getBreakpoint } from "@lululemon/ecom-common/src/lib/breakpoints";
import { composeDataMethods } from "@lululemon/mwa-utils";
export var MAX_WIDTHS = {
    small: 480,
    mobile: 768,
    tablet: 992,
    desktop: 1200,
    large: 1600
};
export var BREAKPOINTS = {
    small: "small",
    mobile: "mobile",
    tablet: "tablet",
    desktop: "desktop",
    large: "large",
    xlarge: "xlarge"
};
export var getCurrentBreakpointByWindowWidth = function() {
    var windowWidth = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : window === null || window === void 0 ? void 0 : window.innerWidth;
    // Breakpoint values were taken from ecom-common/src/style/util/breakpoints.scss
    if (windowWidth < MAX_WIDTHS.small) {
        return BREAKPOINTS.small;
    }
    if (windowWidth < MAX_WIDTHS.mobile) {
        return BREAKPOINTS.mobile;
    }
    if (windowWidth < MAX_WIDTHS.tablet) {
        return BREAKPOINTS.tablet;
    }
    if (windowWidth < MAX_WIDTHS.desktop) {
        return BREAKPOINTS.desktop;
    }
    if (windowWidth < MAX_WIDTHS.large) {
        return BREAKPOINTS.large;
    }
    return BREAKPOINTS.xlarge;
};
export var DeviceTypeContext = /*#__PURE__*/ React.createContext(null);
export function useDeviceType() {
    var context = React.useContext(DeviceTypeContext);
    if (!context) {
        throw new Error("This component must be wrapped in withDeviceCharacteristics.");
    }
    return context;
}
var withDeviceCharacteristics = function(Component) {
    var DeviceCharacteristics = function(_param) {
        var initialDeviceType = _param.deviceType, props = _object_without_properties(_param, [
            "deviceType"
        ]);
        var ref = _sliced_to_array(React.useState(null), 2), currentBreakpoint = ref[0], setCurrentBreakpoint = ref[1];
        var deviceType = React.useMemo(function() {
            if (currentBreakpoint) {
                return {
                    isMobile: isMobile(currentBreakpoint),
                    isDesktop: isDesktop(currentBreakpoint),
                    isTablet: isTablet(currentBreakpoint)
                };
            }
            return initialDeviceType;
        }, [
            currentBreakpoint,
            initialDeviceType
        ]);
        var setBreakpoint = function() {
            var breakpointByWindowWidth = getCurrentBreakpointByWindowWidth();
            if (breakpointByWindowWidth !== currentBreakpoint) {
                setCurrentBreakpoint(breakpointByWindowWidth);
            }
        };
        var debouncedSetBreakpoint = debounce(setBreakpoint, 10);
        React.useEffect(function() {
            setBreakpoint();
            window.addEventListener("resize", debouncedSetBreakpoint);
            return function() {
                return window.removeEventListener("resize", debouncedSetBreakpoint);
            };
        }, []);
        var contextProps = {
            currentBreakpoint: currentBreakpoint,
            deviceType: deviceType
        };
        return /*#__PURE__*/ _jsx(DeviceTypeContext.Provider, {
            value: contextProps,
            children: /*#__PURE__*/ _jsx(Component, _object_spread({}, props))
        });
    };
    return composeDataMethods(Component, {
        prepareInitialPropsContext: function prepareInitialPropsContext(appContext) {
            return _async_to_generator(regeneratorRuntime.mark(function _callee() {
                var ctx, ref, ref1, ref2, ref3, ref4, ref5, bp;
                return regeneratorRuntime.wrap(function _callee$(_ctx) {
                    while(1)switch(_ctx.prev = _ctx.next){
                        case 0:
                            ctx = appContext.ctx || appContext;
                            if (!process.browser) {
                                ;
                                ctx.deviceType = (ref = ctx.req) === null || ref === void 0 ? void 0 : ref.deviceType;
                            } else {
                                ;
                                ctx.deviceType = (ref1 = window.__NEXT_DATA__) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.props) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.pageProps) === null || ref3 === void 0 ? void 0 : ref3.deviceType;
                                if (!ctx.deviceType || (ctx === null || ctx === void 0 ? void 0 : (ref4 = ctx.deviceType) === null || ref4 === void 0 ? void 0 : ref4.isDesktop) || (ctx === null || ctx === void 0 ? void 0 : (ref5 = ctx.deviceType) === null || ref5 === void 0 ? void 0 : ref5.fallback)) {
                                    bp = getBreakpoint(true);
                                    ctx.deviceType = {
                                        isMobile: isMobile(bp),
                                        isTablet: isTablet(bp),
                                        isDesktop: isDesktop(bp)
                                    };
                                }
                            }
                        case 2:
                        case "end":
                            return _ctx.stop();
                    }
                }, _callee);
            }))();
        },
        getInitialProps: function(param) {
            var ctx = param.ctx;
            return {
                deviceType: ctx.deviceType
            };
        }
    })(/*#__PURE__*/ React.memo(DeviceCharacteristics));
};
/**
 * The following HOC must be used within a DeviceTypeContext.Provider or a component
 * wrapped with the withDeviceCharacterization HOC
 * @param {React.Component} Component - Component that needs to receive the deviceType and currentBreakpoint props
 * @returns {React.Component} - returns a Wrapped component that will have the deviceType and currentBreakpoint props
 * @example const PDPWithDeviceContextProps = withDeviceContextProps(ProductDetailPageContainer)
 */ export function withDeviceContextProps(Component) {
    return function WithDeviceContextProps(props) {
        return /*#__PURE__*/ _jsx(DeviceTypeContext.Consumer, {
            children: function(deviceTypeContext) {
                return /*#__PURE__*/ _jsx(Component, _object_spread({}, props, deviceTypeContext));
            }
        });
    };
}
export default withDeviceCharacteristics;
